/*
 * Copyright (c) 2024-2026, s0up and the autobrr contributors.
 * SPDX-License-Identifier: GPL-2.0-or-later
 */

import React, { useEffect, useState } from "react";
import { CloudIcon, CheckIcon } from "@heroicons/react/24/outline";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/Button";
import { settingsApi } from "@/api/settings";
import { showToast } from "@/components/common/Toast";

type Unit = "celsius" | "fahrenheit";

export const WeatherSettings: React.FC = () => {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["weather-settings"],
    queryFn: settingsApi.getWeatherSettings,
  });

  const [location, setLocation] = useState("");
  const [unit, setUnit] = useState<Unit>("celsius");

  useEffect(() => {
    if (data) {
      setLocation(data.location ?? "");
      setUnit(data.unit ?? "celsius");
    }
  }, [data]);

  const save = useMutation({
    mutationFn: () => settingsApi.updateWeatherSettings({ location: location.trim(), unit }),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ["weather-settings"] });
      queryClient.invalidateQueries({ queryKey: ["weather"] });
      setLocation(res.location ?? "");
      showToast(
        res.configured ? "Weather location saved" : "Weather widget disabled",
        "success",
        {
          description: res.configured
            ? `${res.location} — updates at the top of every page`
            : "Set a location to show the widget again",
        },
      );
    },
    onError: (err: Error) =>
      showToast("Could not save weather settings", "error", { description: err.message }),
  });

  const dirty =
    !!data && (location.trim() !== (data.location ?? "") || unit !== (data.unit ?? "celsius"));

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h3 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <CloudIcon className="w-6 h-6 text-gray-600 dark:text-gray-400" />
            Weather
          </h3>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            City for the weather shown at the top of every page. Forecast comes from Open-Meteo — no
            account or API key needed.
          </p>
        </div>

        {dirty && (
          <div className="flex items-center gap-2">
            <Button
              onClick={() => save.mutate()}
              disabled={save.isPending}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              <CheckIcon className="w-4 h-4" />
              {save.isPending ? "Saving…" : "Save"}
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                setLocation(data?.location ?? "");
                setUnit(data?.unit ?? "celsius");
              }}
            >
              Cancel
            </Button>
          </div>
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CloudIcon className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
            Location
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <label className="block">
            <span className="block text-sm font-medium text-gray-700 dark:text-gray-300">City</span>
            <input
              type="text"
              value={location}
              disabled={isLoading}
              placeholder="e.g. Amsterdam"
              onChange={(e) => setLocation(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && dirty && !save.isPending) save.mutate();
              }}
              className="mt-1.5 w-full max-w-sm px-3 py-2 border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-white text-sm rounded-lg focus:outline-none focus:ring-1 focus:ring-emerald-500"
            />
            <span className="block mt-1.5 text-xs text-gray-500 dark:text-gray-400">
              Leave empty and save to hide the widget.
              {data?.configured && data.location ? ` Currently: ${data.location}.` : ""}
            </span>
          </label>

          <div>
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Units</span>
            <div className="mt-1.5 grid max-w-sm grid-cols-2 gap-3">
              {(["celsius", "fahrenheit"] as Unit[]).map((u) => (
                <button
                  key={u}
                  type="button"
                  onClick={() => setUnit(u)}
                  className={`rounded-lg p-3 border text-left transition-colors ${
                    unit === u
                      ? "bg-emerald-500/10 border-emerald-400/50"
                      : "bg-gray-200/50 dark:bg-gray-800/50 border-gray-300 dark:border-gray-800 hover:bg-gray-300/50 dark:hover:bg-gray-800"
                  }`}
                >
                  <div className="font-medium text-gray-900 dark:text-white">
                    {u === "celsius" ? "Celsius" : "Fahrenheit"}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">
                    {u === "celsius" ? "°C · km/h" : "°F · km/h"}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
